import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryExportButton,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryThSortable,
  RegistryToolbar,
  paginationWindow,
} from '@shared/ui/RegistryShell';
import {
  useCreateLnaMutation,
  useDeleteLnaMutation,
  useListLnaQuery,
  useUpdateLnaStatusMutation,
  useUpdateLnaVisibilityMutation,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';

const PAGE_SIZE = 25;

const TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'order', label: 'Приказ' },
  { value: 'regulation', label: 'Положение' },
  { value: 'rules', label: 'Регламент' },
  { value: 'instruction', label: 'Инструкция' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'draft', label: 'Черновик' },
  { value: 'active', label: 'Действует' },
  { value: 'expired', label: 'Истёк' },
  { value: 'cancelled', label: 'Отменён' },
];

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'По дате обновления' },
  { value: 'created_at', label: 'По дате добавления' },
  { value: 'title', label: 'По названию' },
  { value: 'effective_from', label: 'По дате вступления' },
  { value: 'expires_at', label: 'По дате окончания' },
  { value: 'status', label: 'По статусу' },
];

const typeLabel: Record<string, string> = {
  order: 'Приказ',
  regulation: 'Положение',
  rules: 'Регламент',
  instruction: 'Инструкция',
};

const statusLabel: Record<string, string> = {
  draft: 'Черновик',
  active: 'Действует',
  expired: 'Истёк',
  cancelled: 'Отменён',
};

function statusTone(s: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'active':
      return 'success';
    case 'expired':
      return 'warning';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('ru-RU');
}

export function Lna() {
  const { can_write } = useAgentPermission('statut');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('updated_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [search, typeFilter, statusFilter, sort, order]);

  const queryArg = useMemo(
    () => ({
      search: search || undefined,
      lna_type: typeFilter || undefined,
      status: statusFilter || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, typeFilter, statusFilter, sort, order, page],
  );
  const { data, isLoading, isFetching } = useListLnaQuery(queryArg);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/lna/export.csv',
        {
          search,
          lna_type: typeFilter,
          status: statusFilter,
          sort,
          order,
        },
        accessToken,
        'lna.csv',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const [createLna, { isLoading: isCreating }] = useCreateLnaMutation();
  const [updateStatus] = useUpdateLnaStatusMutation();
  const [updateVisibility] = useUpdateLnaVisibilityMutation();
  const [deleteLna] = useDeleteLnaMutation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [lnaType, setLnaType] = useState('order');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [visibleInAssistant, setVisibleInAssistant] = useState(false);

  function toggleSort(field: string) {
    if (sort === field) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setOrder('desc');
    }
  }
  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }
  const pages = paginationWindow(page, totalPages);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await createLna({
      title,
      lna_type: lnaType,
      description: description || undefined,
      owner_department: department || undefined,
      effective_from: effectiveFrom
        ? new Date(`${effectiveFrom}T00:00:00Z`).toISOString()
        : undefined,
      visible_in_assistant: visibleInAssistant,
    }).unwrap();
    setTitle('');
    setDescription('');
    setDepartment('');
    setEffectiveFrom('');
    setLnaType('order');
    setVisibleInAssistant(false);
    setOpen(false);
  }

  async function onActivate(id: string) {
    await updateStatus({ id, status: 'active' }).unwrap();
  }
  async function onToggleVisibility(id: string, current: boolean) {
    await updateVisibility({ id, visible_in_assistant: !current }).unwrap();
  }
  async function onCancel(id: string) {
    if (!window.confirm('Отметить ЛНА как отменённый? Это можно отменить только вручную.')) return;
    await updateStatus({ id, status: 'cancelled' }).unwrap();
  }
  async function onDelete(id: string, t: string) {
    if (!window.confirm(`Удалить «${t}»? Действие необратимо.`)) return;
    await deleteLna(id).unwrap();
  }

  return (
    <>
      <Note>
        Реестр локальных нормативных актов АО «САТКОМ»: приказы, положения, регламенты, инструкции.
        Контроль актуальности по датам вступления/окончания. Шаблоны и LLM-экспертиза проектов ЛНА —
        следующий этап.
      </Note>

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по названию…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <Button
          onClick={() => setOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Новый ЛНА
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {search && (
            <span style={{ fontSize: 12, color: 'var(--osnova-text-muted, #888)' }}>
              По запросу «<strong>{search}</strong>»
            </span>
          )}
          <RegistryExportButton
            type="button"
            onClick={onExport}
            disabled={isExporting || total === 0}
            $disabled={isExporting || total === 0}
            title="Скачать текущую выборку в CSV для Excel"
          >
            {isExporting ? 'Готовим…' : 'Экспорт CSV'}
          </RegistryExportButton>
        </span>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <RegistryThSortable $active={sort === 'title'} onClick={() => toggleSort('title')}>
                Название{sortArrow('title')}
              </RegistryThSortable>
              <th>Тип</th>
              <th>Подразделение</th>
              <RegistryThSortable
                $active={sort === 'effective_from'}
                onClick={() => toggleSort('effective_from')}
              >
                Вступил{sortArrow('effective_from')}
              </RegistryThSortable>
              <RegistryThSortable
                $active={sort === 'expires_at'}
                onClick={() => toggleSort('expires_at')}
              >
                Истекает{sortArrow('expires_at')}
              </RegistryThSortable>
              <RegistryThSortable $active={sort === 'status'} onClick={() => toggleSort('status')}>
                Статус{sortArrow('status')}
              </RegistryThSortable>
              <th>В Помощнике</th>
              <th style={{ width: 240 }} />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={120} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td />
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={8}>
                  {search || typeFilter || statusFilter
                    ? 'По заданным фильтрам ничего не найдено.'
                    : 'Реестр пуст. Добавьте первый ЛНА.'}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.title}</div>
                    {r.description && (
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--osnova-text-muted, #888)',
                          marginTop: 2,
                          maxWidth: 360,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge>{typeLabel[r.lna_type] ?? r.lna_type}</Badge>
                  </td>
                  <td>{r.owner_department ?? '—'}</td>
                  <td>{fmtDate(r.effective_from)}</td>
                  <td>{fmtDate(r.expires_at)}</td>
                  <td>
                    <Badge $tone={statusTone(r.status)}>{statusLabel[r.status] ?? r.status}</Badge>
                  </td>
                  <td>
                    <Button
                      $size="sm"
                      $variant="ghost"
                      onClick={() => onToggleVisibility(r.id, Boolean(r.visible_in_assistant))}
                      disabled={!can_write}
                      title={
                        !can_write
                          ? 'Только чтение'
                          : r.visible_in_assistant
                            ? 'Открыт для всех сотрудников в ОСНОВА.Помощник (клик — закрыть)'
                            : 'Закрыт от сотрудников в ОСНОВА.Помощник (клик — открыть)'
                      }
                    >
                      <Badge $tone={r.visible_in_assistant ? 'info' : 'neutral'}>
                        {r.visible_in_assistant ? 'Открыт всем' : 'Закрыт'}
                      </Badge>
                    </Button>
                  </td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    {r.status === 'draft' && (
                      <Button
                        $size="sm"
                        $variant="secondary"
                        onClick={() => onActivate(r.id)}
                        disabled={!can_write}
                        title={!can_write ? 'Только чтение' : undefined}
                      >
                        Активировать
                      </Button>
                    )}
                    {r.status === 'active' && (
                      <Button
                        $size="sm"
                        $variant="secondary"
                        onClick={() => onCancel(r.id)}
                        disabled={!can_write}
                        title={!can_write ? 'Только чтение' : undefined}
                      >
                        Отменить
                      </Button>
                    )}
                    <Button
                      $size="sm"
                      $variant="secondary"
                      onClick={() => onDelete(r.id, r.title)}
                      disabled={!can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const gap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {gap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <Modal open={open} title="Новый ЛНА" onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <Field>
            <Label htmlFor="lna-title">Название *</Label>
            <Input
              id="lna-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="напр., Приказ о вводе в действие Положения о закупках"
              required
            />
          </Field>
          <Field>
            <Label htmlFor="lna-type">Тип</Label>
            <Select id="lna-type" value={lnaType} onChange={(e) => setLnaType(e.target.value)}>
              <option value="order">Приказ</option>
              <option value="regulation">Положение</option>
              <option value="rules">Регламент</option>
              <option value="instruction">Инструкция</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="lna-desc">Краткое описание</Label>
            <Input
              id="lna-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="lna-dept">Подразделение-владелец</Label>
            <Input
              id="lna-dept"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="напр., Отдел закупок"
            />
          </Field>
          <Field>
            <Label htmlFor="lna-eff">Дата вступления</Label>
            <Input
              id="lna-eff"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </Field>
          <Field>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={visibleInAssistant}
                onChange={(e) => setVisibleInAssistant(e.target.checked)}
              />
              Открыть для всех сотрудников в ОСНОВА.Помощник (по умолчанию ЛНА закрыт)
            </label>
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="submit"
              disabled={isCreating || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating ? 'Сохраняем…' : 'Создать'}
            </Button>
            <Button type="button" $variant="secondary" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
